import * as Sentry from '@sentry/vue';
import { SessionExpiredError } from '@/feature/auth/errors/SessionExpiredError';

const state = {
  code: undefined
};

const mutations = {
  setCode(state, payload) {
    state.code = payload;
  }
};

const actions = {
  /**
   * Global Error Handler
   * @param {Object} payload.error - The actual error object (Axios error or JS error)
   * @param {String} payload.code - The hex error code (e.g., '0x00000067')
   * @param {Object} payload.context - Additional debug data (endpoint, params, etc.)
   * @param {Boolean} payload.showModal - Whether to trigger the UI dialog
   */
  handleError({ dispatch, commit, rootState }, payload) {
    const errorData = payload.error || payload;

    // Suppress modal + telemetry during session-expiry teardown: SessionExpiredError
    // is the sentinel thrown by tokenRefresh middleware; AbortError comes from the
    // cascade of in-flight requests cancelled by store.$abortRequests() in auth/logout.
    if (errorData instanceof SessionExpiredError || errorData?.name === 'AbortError') {
      return;
    }

    const errorCode = payload.code || 'UNKNOWN_CODE';

    Sentry.withScope(scope => {
      // Set level and tag for easy filtering in Sentry Dashboard
      scope.setLevel('error');
      scope.setTag('internal_error_code', errorCode);

      // Attach the Access Token and Context to the "debug_info" context block
      scope.setContext('soulody_context', {
        ...payload.context,
        errorCode: errorCode,
        // Pull token directly from the soulodyApi module state
        currentAccessToken: rootState.soulodyApi?.accessToken || 'No token in state',
        userEmail: rootState.auth?.user?.email || 'Guest',
        url: window.location.href,
        environment: import.meta.env.MODE
      });

      // Ensure we are capturing a proper stack trace
      if (errorData instanceof Error) {
        Sentry.captureException(errorData);
      } else {
        // If it's just a string or object, wrap it to force a stack trace generation
        const syntheticError = new Error(errorData.message || JSON.stringify(errorData));
        Sentry.captureException(syntheticError);
      }
    });

    // FLUTTER / LOGCAT LOGGING
    // We log the critical bits to Flutter for real-time device debugging
    // Compatible with both axios errors (.response.status) and HttpError (.status)
    const httpStatus = errorData.status ?? errorData.response?.status ?? 'N/A';
    const rawData = errorData.data || errorData.response?.data;
    const httpData = rawData ? JSON.stringify(rawData) : 'N/A';

    this.$logToFlutter({
      level: 'E',
      message: `--> [SPA ERROR] CODE: ${errorCode} | MSG: ${errorData.message}`
    });

    if (errorData.response || errorData.status) {
      this.$logToFlutter({
        level: 'E',
        message: `--> [HTTP] STATUS: ${httpStatus} | RESPONSE: ${httpData}`
      });
    }

    // Stack traces are vital for the 5th-time fix; don't stringify them twice
    this.$logToFlutter({
      level: 'E',
      message: `--> [STACK]: ${errorData.stack || 'No stack trace available'}`
    });

    if (payload.showModal !== false) {
      console.error(`[Soulody api] Critical Error ${errorCode}:`, errorData);

      if (payload.code) {
        this.$log('ErrorModal', `Displaying modal for code: ${payload.code}`, 'E');
        commit('setCode', payload.code);
      }

      dispatch('profile/dashboard/setDialog', 'error', { root: true });
    }
  }
};

export const errorModal = {
  state,
  mutations,
  actions,
  namespaced: true
};
